/**
 * Search or jump to (Ctrl+K): channels, people and a few actions, in the
 * prototype's palette look (nx-pal). Only real data: what READY sent.
 */
import { ChannelType, isVoiceChannelType, normalizeForSearch } from '@neosocial/protocol';
import { useMemo, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { useData } from '../store/data';
import {
  activeGuildId,
  channelsInSidebarOrder,
  displayName,
  privateChannelName,
  privateChannels,
  railGuildIds,
} from '../store/selectors';
import { chooseTheme } from '../store/look';
import { goHub, openSettings } from '../store/ui';
import { Avatar, Icon, type IconName } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { openDm } from '../hub/social';
import { joinVoiceFromUi } from '../hub/voiceUi';
import { channelIcon } from '../hub/Hub';

const useJump = create<{ open: boolean }>(() => ({ open: false }));

export function openJump(_anchor?: Element | null): void {
  useJump.setState({ open: true });
}

interface Item {
  group: 'channels' | 'people' | 'actions';
  key: string;
  label: string;
  sub?: string;
  icon?: IconName;
  node?: ReactNode;
  kbd?: string;
  run: () => void;
}

function score(label: string, q: string): number {
  if (!q) return 1;
  const a = normalizeForSearch(label);
  const b = normalizeForSearch(q);
  if (a === b) return 100;
  if (a.startsWith(b)) return 80;
  const i = a.indexOf(b);
  if (i > 0) return 60 - Math.min(i, 30);
  return 0;
}

export function JumpDialog() {
  const open = useJump((s) => s.open);
  if (!open) return null;
  return <JumpBody />;
}

function JumpBody() {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const data = useData();
  const close = () => useJump.setState({ open: false });

  const items = useMemo(() => {
    const out: Item[] = [];
    const gid = activeGuildId(data);
    // the open server's channels in the sidebar's order, then every other
    // server's in the rail's, named by their server (Discord's Quick Switcher)
    for (const g of [gid, ...railGuildIds(data).filter((x) => x !== gid)]) {
      const server = g !== gid && g ? data.guilds[g]?.name : undefined;
      for (const c of channelsInSidebarOrder(data, g)) {
        // voice and stage channels: the speaker, and a click joins (as the sidebar)
        const voice = isVoiceChannelType(c.type);
        const parent = c.parent_id ? data.channels[c.parent_id]?.name : undefined;
        out.push({
          group: 'channels',
          key: c.id,
          label: c.name ?? '',
          sub: [parent, server].filter(Boolean).join(' · ') || undefined,
          icon: channelIcon(c),
          run: () => {
            if (voice) void joinVoiceFromUi(c.id);
            else goHub({ kind: 'channel', channelId: c.id });
          },
        });
      }
    }
    for (const c of privateChannels(data)) {
      out.push({
        group: 'channels',
        key: c.id,
        label: privateChannelName(data, c),
        icon: c.type === ChannelType.GROUP_DM ? 'users' : 'at',
        run: () => goHub({ kind: 'home', channelId: c.id }),
      });
    }
    const members = gid ? (data.members[gid] ?? {}) : {};
    for (const m of Object.values(members)) {
      if (m.user.id === data.me?.id) continue;
      out.push({
        group: 'people',
        key: m.user.id,
        label: displayName(m.user, m),
        sub: '@' + m.user.username,
        node: <Avatar user={m.user} size={24} />,
        run: () => void openDm(m.user.id),
      });
    }
    out.push(
      {
        group: 'actions',
        key: 'settings',
        label: t('act.settings'),
        icon: 'gear',
        kbd: 'Ctrl ,',
        run: () => openSettings('appearance'),
      },
      {
        group: 'actions',
        key: 'theme-l',
        label: t('act.theme.light'),
        icon: 'sun',
        run: () => void chooseTheme('pearl'),
      },
      {
        group: 'actions',
        key: 'theme-d',
        label: t('act.theme.dark'),
        icon: 'moon',
        run: () => void chooseTheme('smoked'),
      },
    );
    return out;
  }, [data, t]);

  const shown = useMemo(() => {
    const query = q.trim().replace(/^[#@>]/, '');
    const only = ({ '#': 'channels', '@': 'people', '>': 'actions' } as const)[
      q.trim().charAt(0) as '#' | '@' | '>'
    ];
    const scored = items
      .map((x) => ({ x, s: score(x.label, query) + (x.sub ? score(x.sub, query) * 0.3 : 0) }))
      .filter((y) => y.s > 0 && (!only || y.x.group === only))
      .sort((a, b) => b.s - a.s);
    const groups: Item['group'][] = ['channels', 'people', 'actions'];
    const res: Item[] = [];
    for (const g of groups)
      res.push(
        ...scored
          .filter((y) => y.x.group === g)
          .slice(0, only ? 10 : query ? 6 : 4)
          .map((y) => y.x),
      );
    return res;
  }, [items, q]);

  const go = (i: number) => {
    const it = shown[i];
    if (!it) return;
    close();
    setTimeout(it.run, 10);
  };
  const cur = Math.min(sel, Math.max(0, shown.length - 1));
  let lastGroup = '';

  return (
    <Dialog label={t('ns.jump.title')} cls="nx-pal-dlg" wrapCls="top" onClose={close}>
      <div className="nx-pal">
        <div className="nx-pal-field">
          <Icon name="search" />
          <input
            type="text"
            dir="auto"
            className="nx-pal-in"
            role="combobox"
            aria-expanded="true"
            aria-controls="nx-pal-list"
            aria-activedescendant={shown.length ? `nx-pal-o${cur}` : undefined}
            autoComplete="off"
            spellCheck={false}
            data-autofocus
            placeholder={t('ns.jump.placeholder')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSel((cur + 1) % Math.max(1, shown.length));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSel((cur - 1 + shown.length) % Math.max(1, shown.length));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                go(cur);
              }
            }}
          />
          <kbd className="nx-kbd">Esc</kbd>
        </div>
        <div
          className="nx-pal-list nx-scroll"
          id="nx-pal-list"
          role="listbox"
          aria-label={t('ns.jump.title')}
        >
          {shown.length === 0 ? (
            <div className="nx-pal-empty">
              <Icon name="search" />
              <p>{t('pal.empty', { q: q.trim() })}</p>
            </div>
          ) : (
            shown.map((x, i) => {
              const head =
                x.group !== lastGroup ? (
                  <div className="nx-pal-g" role="presentation">
                    {t(`pal.${x.group}`)}
                  </div>
                ) : null;
              lastGroup = x.group;
              return (
                <div key={x.group + x.key}>
                  {head}
                  <div
                    className="nx-pal-row"
                    role="option"
                    id={`nx-pal-o${i}`}
                    aria-selected={i === cur}
                    onPointerMove={() => i !== cur && setSel(i)}
                    onClick={() => go(i)}
                  >
                    <span className="ic">{x.node ?? <Icon name={x.icon ?? 'arrow-right'} />}</span>
                    <span className="tx">
                      <span className="l" dir="auto">
                        {x.label}
                      </span>
                      {x.sub ? (
                        <span className="s" dir="auto">
                          {x.sub}
                        </span>
                      ) : null}
                    </span>
                    {x.kbd ? <kbd className="nx-kbd">{x.kbd}</kbd> : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="nx-pal-foot">
          <span>
            <kbd className="nx-kbd">↑</kbd>
            <kbd className="nx-kbd">↓</kbd> {t('pal.hint.move')}
          </span>
          <span>
            <kbd className="nx-kbd">Enter</kbd> {t('pal.hint.open')}
          </span>
          <span>
            <kbd className="nx-kbd">Esc</kbd> {t('pal.hint.close')}
          </span>
        </div>
      </div>
    </Dialog>
  );
}
